/**
 * Homeowner view-only sharing — shared contracts.
 *
 * Frozen boundary between the 3 downstream build agents (backend, modal,
 * viewer). See `claude-docs/2026-06-08-homeowner-viewing-contracts.md`.
 * Do not change names/shapes here without updating that doc.
 *
 * v1 model: the homeowner lands DIRECTLY in first-person (no orbit), spawned
 * feature-anchored in the largest "remodel" room. The marked-room set lives on
 * the share (`remodelRoomIds`), not on the room docs.
 */
import { calculatePolygonArea } from '../../utils/scaleUtils';

// --- Routes (public, no PrivateRoute) -------------------------------------

/** Default-landing route — first-person in the largest marked room. */
export const HOMEOWNER_HOME_PATH = '/h/:token';

/** First-person route for a specific marked room (room switcher target). */
export const HOMEOWNER_ROOM_PATH = '/h/:token/room/:roomId';

/** Build the public default-landing URL for a share token. */
export const buildHomeownerHomeUrl = (token) => `/h/${token}`;

/** Build the public first-person URL for a share token + room id. */
export const buildHomeownerRoomUrl = (token, roomId) =>
  `/h/${token}/room/${roomId}`;

// --- Landing selection ----------------------------------------------------

/**
 * Pick the default landing room: the largest-floor-area room among the marked
 * remodel rooms. Pure. Returns the room id, or null if none resolve.
 *
 * @param {Array<{id:string, floors?:Array<{polygonCorners?:Array<{x:number,z:number}>}>}>} rooms
 *        The structure's rooms.
 * @param {string[]} remodelRoomIds  The marked-room set from the share.
 * @returns {string|null}
 */
export const selectLandingRoomId = (rooms, remodelRoomIds) => {
  const marked = new Set(remodelRoomIds || []);
  let bestId = null;
  let bestArea = -1;
  for (const room of rooms || []) {
    if (!room?.id || !marked.has(room.id)) continue;
    const corners = room.floors?.[0]?.polygonCorners || [];
    // calculatePolygonArea wants [x, z] tuples (world X-Z plane).
    const area =
      corners.length >= 3
        ? calculatePolygonArea(corners.map((c) => [c.x, c.z]))
        : 0;
    if (area > bestArea) {
      bestArea = area;
      bestId = room.id;
    }
  }
  return bestId;
};

// --- Share-resolve error mapping ------------------------------------------

/**
 * Whether a share page failed at the anonymous sign-in, before the token was
 * ever looked at. Firebase Auth client errors are prefixed `auth/` (e.g.
 * `auth/admin-restricted-operation` when the project has Anonymous sign-in
 * turned off). Such a link may be perfectly good, so it must not read as
 * "invalid". Pure.
 *
 * @param {{code?: string}} err
 * @returns {boolean}
 */
export const isShareSignInError = (err) =>
  (err?.code || '').startsWith('auth/');

/** What a share page says when `isShareSignInError` — not the link's fault. */
export const SHARE_SIGN_IN_FAILED_MESSAGE =
  "We couldn't open this link right now. Please try again in a little while.";

/**
 * Map a `resolveHomeownerShare` failure to a viewer error state. The backend
 * rejects a REVOKED link with `permission-denied` (Firebase callable client
 * error codes are prefixed, e.g. `functions/permission-denied`), which should
 * read as "turned off by the contractor"; a failed anonymous sign-in is
 * `unavailable` (the token was never checked); any other failure (not-found,
 * network, etc.) is a generic invalid/missing link. Pure.
 *
 * @param {{code?: string}} err
 * @returns {'revoked'|'unavailable'|'invalid'}
 */
export const mapResolveShareError = (err) => {
  if ((err?.code || '').includes('permission-denied')) return 'revoked';
  if (isShareSignInError(err)) return 'unavailable';
  return 'invalid';
};

/**
 * The sentence a share page shows for a `mapResolveShareError` state. Each
 * page words its own `revoked` / `invalid` lines ("proposal link", "design
 * link"); `unavailable` is the same everywhere. Pure.
 *
 * @param {'revoked'|'unavailable'|'invalid'} error
 * @param {{ revoked: string, invalid: string }} messages
 * @returns {string}
 */
export const shareErrorMessage = (error, { revoked, invalid }) => {
  if (error === 'revoked') return revoked;
  if (error === 'unavailable') return SHARE_SIGN_IN_FAILED_MESSAGE;
  return invalid;
};

// --- Typedefs (plain JS — JSDoc only) -------------------------------------

/**
 * @typedef {Object} HomeownerShare
 * @property {string}   id             Firestore doc id.
 * @property {string}   token          Opaque public URL segment (uuid).
 * @property {string}   projectId      Shared project id.
 * @property {string}   structureId    Structure rendered (contractor-picked).
 * @property {string[]} remodelRoomIds Marked rooms set at share time; the room-switcher set. Landing = largest of these.
 * @property {string}   createdBy      uid of the contractor who minted the link.
 * @property {string}   createdAt      ISO string / serverTimestamp.
 * @property {boolean}  enabled        false = revoked.
 * @property {string}   shareType      'homeowner' discriminator.
 * @property {string[]} claimedByUids  Future-account seam; empty for anon links.
 */

/**
 * @typedef {Object} ViewerCanvasProps
 * @property {object}   structureData   Resolved structure JSON to render (live-subscribed).
 * @property {string}   spawnRoomId     Room to spawn into, feature-anchored (default = largest marked room).
 * @property {string[]} remodelRoomIds  Marked rooms; the room-switcher set. Unmarked rooms are walk-only.
 * @property {(roomId: string) => void} onSwitchRoom  Switcher selection → navigate to that room route + re-spawn.
 * @property {boolean}  [controlsHidden] The walk pad, aim dot and minimap are off screen — a host view covers the canvas.
 */
